import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useMeConversations,
  useCreateConversation,
  useUpdateConversation,
} from './useConversations';
import { useMeAgents } from './useAgents';
import { isPlatformEntity } from '../types';
import { routes } from '../lib/routes';

const WELCOME_AGENT_NAME = 'Operator';
const WELCOME_CHAT_NAME = 'Welcome chat';

/**
 * Auto-create a starter chat for users with an empty conversations list.
 *
 * Picks the platform `Operator` agent and names the conversation "Welcome
 * chat". Re-fires if the user's list becomes empty again (e.g. they deleted
 * the welcome) — the ref resets whenever the list transitions back to
 * non-empty. Failures (e.g. spaceId not yet resolvable) are swallowed and
 * retried on the next empty transition.
 */
export function useEnsureWelcomeChat() {
  const { data: conversationsData, isLoading: conversationsLoading } =
    useMeConversations();
  const { data: agentsData, isLoading: agentsLoading } = useMeAgents();
  const createConversation = useCreateConversation();
  const updateConversation = useUpdateConversation();
  const navigate = useNavigate();
  const triedForCurrentEmptyRef = useRef(false);

  useEffect(() => {
    if (conversationsLoading || agentsLoading) return;

    const conversations = conversationsData?.items ?? [];
    if (conversations.length > 0) {
      triedForCurrentEmptyRef.current = false;
      return;
    }
    if (triedForCurrentEmptyRef.current) return;

    const operator = (agentsData?.items ?? []).find(
      (a) => isPlatformEntity(a) && a.name === WELCOME_AGENT_NAME,
    );
    if (!operator) return;

    triedForCurrentEmptyRef.current = true;
    (async () => {
      try {
        const created = await createConversation.mutateAsync({
          agentId: operator.agentId,
          persistent: true,
          origin: 'user',
        });
        await updateConversation.mutateAsync({
          conversationId: created.conversationId,
          input: { name: WELCOME_CHAT_NAME },
        });
        navigate(routes.operator.chat(created.conversationId));
      } catch {
        // Best-effort. The next empty-state transition will retry.
      }
    })();
  }, [
    conversationsData,
    conversationsLoading,
    agentsData,
    agentsLoading,
    createConversation,
    updateConversation,
    navigate,
  ]);
}
